'use client';

import { useCallback, useState } from 'react';

type TooltipPosition = {
  x: number;
  y: number;
};

/**
 * Hook to manage tooltips for grammar feedback
 * Handles tooltip positioning and active feedback state
 */
export default function useTooltipManager<T>() {
  const [activeFeedback, setActiveFeedback] = useState<T | null>(null);
  const [position, setPosition] = useState<TooltipPosition>({ x: 0, y: 0 });

  // Update tooltip position based on mouse event
  const updatePosition = useCallback((e: React.MouseEvent, containerRef: React.RefObject<HTMLElement | null>) => {
    if (containerRef.current) {
      const rect = containerRef.current.getBoundingClientRect();
      setPosition({
        x: e.clientX - rect.left,
        y: e.clientY - rect.top,
      });
    }
  }, []);

  // Clear active feedback (e.g. on mouse leave or focus)
  const clearActiveFeedback = useCallback(() => {
    setActiveFeedback(null);
  }, []);

  // Handle mouse enter on a feedback item
  const handleFeedbackHover = useCallback((
    e: React.MouseEvent,
    feedback: T,
    containerRef: React.RefObject<HTMLElement | null>,
    isFocused: boolean,
  ) => {
    if (isFocused === false) {
      setActiveFeedback(feedback);
      updatePosition(e, containerRef);
    }
  }, [updatePosition]);

  // Handle mouse move on a feedback item
  const handleFeedbackMove = useCallback((
    e: React.MouseEvent,
    feedback: T,
    containerRef: React.RefObject<HTMLElement | null>,
    isFocused: boolean,
  ) => {
    if (isFocused === false && activeFeedback === feedback) {
      updatePosition(e, containerRef);
    }
  }, [activeFeedback, updatePosition]);

  return {
    activeFeedback,
    setActiveFeedback,
    position,
    updatePosition,
    clearActiveFeedback,
    handleFeedbackHover,
    handleFeedbackMove,
  };
}
